import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { CONTACT_RESULTS, OUTCOMES } from '@/features/cases/dispositionLabels'
import { VisitPhoto } from '@/features/cases/VisitPhoto'
import { dateTime, humanizeCode, inr } from '@/lib/format'
import { useLabel } from '@/lib/labels'
import { Link } from '@/lib/router'

import { usePlanSummary, type PlanSummary, type PlanVisit } from './api'

/**
 * The cases visited on this plan, in the order the agent reached them, each
 * with what was recorded at the door — outcome, the disposition it wrote,
 * the agent's notes, whether it was held, its follow-up — and the photo, so
 * a manager can verify the visits from one page.
 */
export function TripVisits({ tripId, isActive }: { tripId: string; isActive: boolean }) {
  const summary = usePlanSummary(tripId, { isActive })
  const visits = summary.data ? visitedInOrder(summary.data) : []
  const collectedByCase = summary.data ? collectedPerCase(summary.data) : new Map<string, number>()

  return (
    <Card>
      <CardHeader className="space-y-1">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Visits</h2>
        <p className="text-xs text-muted-foreground">
          {summary.data
            ? `${summary.data.stopsVisited} of ${summary.data.stopsPlanned} planned stops visited`
              + (summary.data.unplanned.length > 0 ? `, ${summary.data.unplanned.length} unplanned` : '')
              + ' · in the order visited'
            : summary.isError
              ? "Couldn't load the visits."
              : 'Loading visits…'}
        </p>
      </CardHeader>
      <CardContent>
        {summary.data && visits.length === 0 && (
          <p className="text-sm text-muted-foreground">No visits marked on this plan yet.</p>
        )}
        {visits.length > 0 && (
          <ul className="divide-y divide-slate-200">
            {visits.map(({ visit, planned }) => (
              <VisitItem
                key={visit.visitId}
                visit={visit}
                planned={planned}
                collected={collectedByCase.get(visit.caseId)}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function VisitItem({ visit: v, planned, collected }: { visit: PlanVisit; planned: boolean; collected: number | undefined }) {
  const label = useLabel()
  return (
    <li className="flex gap-4 py-3">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/cases/$caseId"
            params={{ caseId: v.caseId }}
            className="font-mono text-xs text-navy-900 underline-offset-4 hover:underline"
          >
            {v.loanAccountNo ?? v.caseId.slice(0, 8)}
          </Link>
          {v.customerName && <span className="text-sm font-medium text-navy-900">{v.customerName}</span>}
          {!planned && <Badge tone="info">Unplanned</Badge>}
          {v.approvalStatus !== 'NOT_REQUIRED' && (
            <Badge tone={v.approvalStatus === 'APPROVED' ? 'positive' : v.approvalStatus === 'HELD' ? 'warning' : 'negative'}>
              {label('visitApproval', v.approvalStatus)}
            </Badge>
          )}
        </div>
        <p className="text-sm text-navy-900">
          {label('visitOutcome', v.outcome)} · {label('visitReason', v.visitReason)}
          <span className="ml-2 text-xs text-muted-foreground">{dateTime(v.occurredAt)}</span>
        </p>
        {v.contactResult && (
          <p className="text-xs text-muted-foreground">
            Disposition: {wordFor(CONTACT_RESULTS, v.contactResult)}
            {v.dispositionOutcome && ` · ${wordFor(OUTCOMES, v.dispositionOutcome)}`}
            {v.dispositionCode && <span className="ml-1 font-mono">({v.dispositionCode})</span>}
          </p>
        )}
        {v.notes && <p className="text-xs text-navy-900">“{v.notes}”</p>}
        {v.heldReason && <p className="text-xs text-warning">Held: {v.heldReason}</p>}
        <FollowUp visit={v} collected={collected} />
      </div>
      <div className="shrink-0">
        {v.hasPhoto ? (
          <VisitPhoto visitId={v.visitId} open thumbnail />
        ) : (
          <p className="w-32 text-xs text-muted-foreground">No photo on this visit.</p>
        )}
      </div>
    </li>
  )
}

/** What the outcome required next, and whether it happened. */
function FollowUp({ visit: v, collected }: { visit: PlanVisit; collected: number | undefined }) {
  if (v.followUpDone === null) return null
  if (v.outcome === 'PAID') {
    return v.followUpDone && collected
      ? <p className="text-xs text-positive">Collected {inr(collected)} during the plan</p>
      : v.followUpDone
        ? <p className="text-xs text-positive">Collection recorded</p>
        : <p className="text-xs text-warning">Collection missing</p>
  }
  return v.followUpDone
    ? <p className="text-xs text-positive">New PTP recorded</p>
    : <p className="text-xs text-warning">PTP missing</p>
}

/** Planned stops that were visited and the unplanned visits, earliest first. */
function visitedInOrder(s: PlanSummary): Array<{ visit: PlanVisit; planned: boolean }> {
  const planned = s.stops.flatMap((stop) => (stop.visit ? [{ visit: stop.visit, planned: true }] : []))
  const unplanned = s.unplanned.map((visit) => ({ visit, planned: false }))
  return [...planned, ...unplanned].sort((a, b) => a.visit.occurredAt.localeCompare(b.visit.occurredAt))
}

function wordFor(list: { value: string; label: string }[], code: string): string {
  return list.find((o) => o.value === code)?.label ?? humanizeCode(code)
}

function collectedPerCase(s: PlanSummary): Map<string, number> {
  const by = new Map<string, number>()
  for (const c of s.collections) by.set(c.caseId, (by.get(c.caseId) ?? 0) + Number(c.amount))
  return by
}
